import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader } from 'src/@vymo/ui/atoms';
import { Banner } from 'src/@vymo/ui/blocks';
import Table from 'src/@vymo/ui/blocks/table';
import { Page } from 'src/@vymo/ui/molecules';
import { ReactComponent as ArrowDownWithUnderline } from 'src/assets/icons/arrow_down_underline.svg';
import { ReactComponent as ArrowUpWithUnderline } from 'src/assets/icons/arrow_up_underline.svg';
import { getGrowthBookFeatureFlag } from 'src/featureFlags';
import { Keys, locale } from 'src/i18n';
import { getEntityListParams } from 'src/models/details/selectors';
import { saveTableParams, setCurrentEntityId } from 'src/models/details/slice';
import { getFiltersByContext } from 'src/models/filters/selectors';
import { getRevyreWorkflowConfig } from 'src/models/lmsConfig/selectors';
import {
  clearModuleButtons,
  setModuleButtons,
} from 'src/models/moduleButtons/slice';
import {
  REVYRE_DETAILS,
  REVYRE_DETAILS_360,
  REVYRE_FILTERS,
  REVYRE_FORM,
  REVYRE_WORKFLOW_DETAILS,
  REVYRE_WORKFLOW_DETAILS_360,
  UPLOAD_FORM,
} from 'src/modules/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  ANALYTICS_TYPE_REVYRE_CREATE_INITIATED,
  ANALYTICS_TYPE_REVYRE_DETAILS_OPENED,
  ANALYTICS_TYPE_REVYRE_LIST,
} from 'src/workspace/analytics/constants';
import { trackAnalyticsActions } from 'src/workspace/analytics/queries';
import { setAxiosErrorDescriptionHandler } from 'src/workspace/axios';
import ChildComponent from 'src/workspace/childComponent';
import { renderDialog, renderModule } from 'src/workspace/slice';
import { getClientConfigData } from 'src/workspace/utils';
import { REVYRE } from './constants';
import { BULK_REVOKE, BULK_UPLOAD } from './ddp/constants';
import { getPrioritizedFilters } from './queries';
import {
  getCreateEntityFabName,
  getEntityList,
  getEntityListFilters,
  getEntityListMetaData,
  getIsDialogOpen,
  getIsLoading,
  getListColumns,
  getNotification,
} from './selectors';
import { setEntityListMetadata, setError } from './slice';
import {
  fetchBulkAllocationTypes,
  fetchBulkRevocationTypes,
  fetchEntityList,
  triggerBulkDownloadJob,
} from './thunk';
import { DomainType, WorkflowType } from './types';
import styles from './index.module.scss';

// eslint-disable-next-line complexity
function RevyreList({
  domainType,
  // @ts-ignore
  workflowType = '',
  selectedEntities: selectedEntitiesProp,
  mode = 'default',
}: {
  domainType: DomainType;
  workflowType: WorkflowType;
  selectedEntities?: string;
  mode: 'default' | 'details';
}) {
  const dispatch = useAppDispatch();
  const listData = useAppSelector(getEntityList);
  const columns = useAppSelector((state) =>
    getListColumns(state, domainType, workflowType),
  );
  const fabName = useAppSelector((state) =>
    getCreateEntityFabName(state, domainType, workflowType),
  );
  const isLoading = useAppSelector(getIsLoading);
  const isDialogOpen = useAppSelector(getIsDialogOpen);
  const { message, id: messageId, variant } = useAppSelector(getNotification);
  const { pageSize, currentPage, maxPages, totalCount } = useAppSelector(
    getEntityListMetaData,
  );

  const [selectedEntities, setSelectedEntities] = useState<
    Record<string, boolean>
  >(selectedEntitiesProp ? { [selectedEntitiesProp]: true } : {});

  const tableParams = useAppSelector(getEntityListParams);
  const newDetailsPage = getGrowthBookFeatureFlag('enable-details-360');

  // Get filters from the filters module
  const filterContext = useMemo(
    () => `revyre_${domainType}_${workflowType || 'default'}`,
    [domainType, workflowType],
  );
  const listFilters = useAppSelector(getEntityListFilters);
  const filtersByContext = useAppSelector((state) =>
    getFiltersByContext(state, filterContext),
  );
  const revyreWorkflowConfig = useAppSelector(getRevyreWorkflowConfig);
  const currentWorkflowConfig =
    revyreWorkflowConfig?.[workflowType || domainType] || {};
  const quickActionAccessAllowed =
    currentWorkflowConfig?.quickActions?.accessAllowed || {};
  const {
    downloadList: shouldShowDownloadListButton,
    bulkReassign: shouldShowBulkReassignButton,
    bulkRevocation: shouldShowBulkRevokeButton,
  } = quickActionAccessAllowed;
  const { LIST_PAGE_CREATE_SCENARIO_BLOCKED_ENTITIES } = REVYRE.COLLECT_IQ;
  const filters = getPrioritizedFilters({
    listFilters,
    filtersByContext,
    tableParamFilters: tableParams.filters,
  });

  const handleOnClick = useCallback(() => {
    trackAnalyticsActions({
      type: ANALYTICS_TYPE_REVYRE_CREATE_INITIATED,
      isPageView: false,
      module: domainType,
    });
    dispatch(
      renderDialog({
        id: REVYRE_FORM,
        props: {
          type: 'create',
          domainType,
        },
      }),
    );
  }, [dispatch, domainType]);

  const handleOnDownloadListClick = useCallback(() => {
    if (totalCount === 0) {
      return;
    }
    const { sortBy, sortType } = tableParams;
    const configData = getClientConfigData();
    const payload = {
      sortOptions: { sortBy, sortType },
      filters: {
        ...filters,
        page: {
          pageSize,
          pageNumber: 1,
        },
      },
      userCode: configData.code,
      clientCode: configData.client,
      jobType: 2,
      count: totalCount,
      maxPages,
      module: workflowType || domainType,
    };

    dispatch(triggerBulkDownloadJob(payload));
  }, [
    tableParams,
    filters,
    pageSize,
    totalCount,
    maxPages,
    workflowType,
    domainType,
    dispatch,
  ]);

  const handleOnBulkReassignClick = () => {
    dispatch(fetchBulkAllocationTypes(workflowType || domainType)).then(() => {
      dispatch(
        renderDialog({
          id: UPLOAD_FORM,
          props: {
            title: locale(Keys.BULK_REASSIGN),
            actionType: BULK_UPLOAD,
            additionalData: { workflowType },
          },
        }),
      );
    });
  };

  const handleOnBulkRevokeClick = () => {
    dispatch(fetchBulkRevocationTypes(workflowType || domainType)).then(() => {
      dispatch(
        renderDialog({
          id: UPLOAD_FORM,
          props: {
            title: locale(Keys.BULK_REVOKE),
            actionType: BULK_REVOKE,
            additionalData: { workflowType },
          },
        }),
      );
    });
  };

  useEffect(() => {
    trackAnalyticsActions({
      type: ANALYTICS_TYPE_REVYRE_LIST,
      isPageView: true,
      module: domainType === 'workflow' ? workflowType : domainType,
    });
  }, [domainType, workflowType]);

  // Set module buttons when component mounts
  useEffect(() => {
    if (mode === 'default') {
      const moduleButtons: any[] = [];

      if (shouldShowDownloadListButton) {
        moduleButtons.push({
          label: locale(Keys.DOWNLOAD_LIST),
          disabled: totalCount === 0,
          onClick: handleOnDownloadListClick,
          variant: 'subtle',
          'data-test-id': 'download-list-button',
          icon: <ArrowDownWithUnderline />,
        });
      }

      if (shouldShowBulkRevokeButton) {
        moduleButtons.push({
          label: locale(Keys.BULK_REVOKE),
          onClick: handleOnBulkRevokeClick,
          variant: 'subtle',
          'data-test-id': 'bulk-revoke-button',
          icon: <ArrowUpWithUnderline />,
        });
      }

      if (shouldShowBulkReassignButton) {
        moduleButtons.push({
          label: locale(Keys.BULK_REASSIGN),
          onClick: handleOnBulkReassignClick,
          variant: 'subtle',
          'data-test-id': 'bulk-reassign-button',
          icon: <ArrowUpWithUnderline />,
        });
      }

      if (
        !LIST_PAGE_CREATE_SCENARIO_BLOCKED_ENTITIES.includes(
          workflowType || domainType,
        )
      ) {
        moduleButtons.push({
          label: fabName,
          onClick: handleOnClick,
          variant: 'default',
          'data-test-id': 'create-entity-button',
        });
      }
      dispatch(setModuleButtons(moduleButtons));
    }
    return () => {
      if (mode === 'default') {
        dispatch(clearModuleButtons());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    mode,
    workflowType,
    domainType,
    fabName,
    handleOnDownloadListClick,
    shouldShowBulkReassignButton,
    shouldShowBulkRevokeButton,
    shouldShowDownloadListButton,
    totalCount,
  ]);

  const onEntitySelection = useCallback(
    (_, selectedRows) => {
      setSelectedEntities(selectedRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(selectedEntities)],
  );

  // Note: For details mode, we might need to add workflowId to filters
  // This can be handled in the filters module or here if needed
  // For now, keeping it simple and not modifying filters for details mode

  useEffect(
    () =>
      setAxiosErrorDescriptionHandler((errorDescription) => {
        dispatch(
          setError({
            message: errorDescription,
            id: String(Date.now()),
          }),
        );
      }),
    [dispatch],
  );

  return (
    <Loader
      visible={isLoading && !isDialogOpen}
      className={styles.entityList__loader}
    >
      {message && (
        <Banner
          key={messageId}
          message={message}
          variant={variant}
          position="topRight"
          duration={5000}
          closeable
        />
      )}
      <Page className={styles.entityList}>
        {mode === 'default' && (
          <Page.Header>
            <div className={styles.entityList__actions}>
              <div className={styles.entityList__actions__right_button_div}>
                <ChildComponent
                  id={REVYRE_FILTERS}
                  props={{ workflowType, domainType }}
                />
              </div>
            </div>
          </Page.Header>
        )}

        <Page.Body className={styles.entityList__body}>
          {columns.length > 0 && (
            <Table
              data-test-id="entity-list"
              classNames={styles.entityList__table}
              isAccordion={false}
              tableData={listData}
              columnConfigs={(columns || []).map((column) => ({
                ...column,
              }))}
              tableConfig={{
                showSearch: false,
                isRowSelectionEnabled: false,
                name: locale(Keys.ENTITY_LIST),
                loading: isLoading,
                pagination: {
                  visible: true,
                  pageSize,
                  totalPages: Math.max(1, Math.ceil(totalCount / pageSize)),
                  currentPage,
                  totalItems: totalCount,
                },
                selections: selectedEntities,
                onRowClick: (rowId) => {
                  dispatch(setCurrentEntityId(rowId));
                  trackAnalyticsActions({
                    type: ANALYTICS_TYPE_REVYRE_DETAILS_OPENED,
                    isPageView: false,
                    module:
                      domainType === 'workflow' ? workflowType : domainType,
                    payload: {
                      entityId: rowId,
                    },
                  });
                  if (domainType === 'workflow') {
                    dispatch(
                      renderModule({
                        id: newDetailsPage
                          ? REVYRE_WORKFLOW_DETAILS_360
                          : REVYRE_WORKFLOW_DETAILS,
                        props: {
                          domainType,
                          workflowType,
                          entityId: rowId,
                        },
                      }),
                    );
                  } else {
                    dispatch(
                      renderModule({
                        id: newDetailsPage
                          ? REVYRE_DETAILS_360
                          : REVYRE_DETAILS,
                        props: {
                          domainType,
                          entityId: rowId,
                        },
                      }),
                    );
                  }
                },
              }}
              onRowSelect={onEntitySelection}
              onSort={(sortedColumns) => {
                const [sortedColumn] = sortedColumns ?? [];

                if (!sortedColumn) return;

                const { id: sortBy, desc } = sortedColumn;
                const sortType = desc ? 'desc' : 'asc';

                dispatch(
                  saveTableParams({
                    sortBy,
                    sortType,
                  }),
                );

                dispatch(
                  fetchEntityList({
                    domainType,
                    payload: {
                      sortBy,
                      sortType,
                      filters,
                      page: {
                        pageSize,
                        pageNumber: 1,
                      },
                    },
                  }),
                );
              }}
              onChangePageSize={(updatedPageSize) => {
                dispatch(setEntityListMetadata({ pageSize: updatedPageSize }));
                dispatch(
                  fetchEntityList({
                    domainType,
                    payload: {
                      sortBy: tableParams.sortBy,
                      sortType: tableParams.sortType,
                      filters,
                      page: {
                        pageSize: updatedPageSize,
                        pageNumber: 1,
                      },
                    },
                  }),
                );
              }}
              onChangePageIndex={(pageIndex) => {
                dispatch(
                  setEntityListMetadata({ currentPage: Number(pageIndex) + 1 }),
                );
                dispatch(
                  fetchEntityList({
                    domainType,
                    payload: {
                      sortBy: tableParams.sortBy,
                      sortType: tableParams.sortType,
                      filters,
                      page: {
                        pageSize,
                        pageNumber: Number(pageIndex) + 1,
                      },
                    },
                  }),
                );
              }}
            />
          )}
        </Page.Body>
      </Page>
    </Loader>
  );
}

export default RevyreList;
